import { useState } from 'react';
import { Link } from 'react-router';
import { OS } from 'types';

import NotificationBell from '~/assets/icons/NotificationBell';
import ProfileIcon from '~/assets/icons/Profile';
import Tracked from '~/components/Tracked';
import RegisterIntentEvent from '~/events/RegisterIntentEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import MenuModal from '~/legacy/components/mobile-header';
import headermenu from '~/legacy/data/main-headermenu';
import mainHeaderMenu from '~/legacy/data/main-mobile-data';
import useWindowSize from '~/legacy/hooks/useWindowSize';
import {
  getIsLargeBreakpoint,
  getIsMediumBreakpoint,
  isSSR,
  modalFrameSemantic
} from '~/legacy/utils';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import Icon from '../Icon';
import StandardModal from '../StandardModal';
import Menu from './Menu';
import SearchPanel from './Search/SearchPanel';

const MainHeader = () => {
  const { isMobileRequest, clientOs, isBot } = useRootData();
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const [searchPanel, setSearchPanel] = useState(false);
  const user = useUser();
  const windowObject = !isSSR() ? window : {};
  const windowSize = useWindowSize(windowObject);
  const { width: windowWidth } = windowSize || {};

  const isAndroidClient = clientOs === OS.Android;
  const isLargeBreakpoint = getIsLargeBreakpoint(windowWidth, isMobileRequest);
  const isMediumBreakpoint = getIsMediumBreakpoint(
    windowWidth,
    isMobileRequest
  );

  const profileIconColor = user.isLoggedIn ? '#56BD6F' : '#A9A9A9';
  const profilePage = '/profile/';

  return (
    <header className="main-header sticky top-0" role="banner">
      <div className="content-width w-full flex justify-center">
        <section className="main-header__container">
          <Link to="/" className="main-header__logo-link">
            <Icon
              name="sole-icon"
              width="50"
              height="50"
              viewBox="0 0 58 44"
              fill="#FFFFFF"
              className="main-header__logo"
            />
          </Link>

          <div className="lg:hidden">
            <MenuModal
              menu={mainHeaderMenu}
              mobileMenuIsOpen={mobileMenuIsOpen}
              setMobileMenuIsOpen={setMobileMenuIsOpen}
            />
          </div>
          <div className="hidden lg:block">
            <Menu menu={headermenu} />
          </div>
        </section>

        <section className="flex gap-5 items-center">
          <div className="main-header__search">
            <div className="lg:hidden">
              <Icon
                name="search"
                onClick={() => {
                  setSearchPanel(true);
                }}
                className="main-header__search-button"
                width="19"
                height="19"
                viewBox="0 0 1792 1792"
                fill="#a9a9a9"
              />
              <span className="hc-srt">Search</span>
              {searchPanel && (
                <StandardModal
                  isFullScreen
                  isFullBody
                  background={modalFrameSemantic.CONCRETE}
                  onCloseClick={() => {}}
                  preventBodyScrollLock
                  disableClickOutside
                  hideClose
                >
                  <SearchPanel
                    searchPanelState={[searchPanel, setSearchPanel]}
                    isLargeBreakpoint={!!isLargeBreakpoint}
                    isMediumBreakpoint={!!isMediumBreakpoint}
                  />
                </StandardModal>
              )}
            </div>

            <div className="hidden lg:block">
              <SearchPanel
                searchPanelState={[searchPanel, setSearchPanel]}
                isLargeBreakpoint={!!isLargeBreakpoint}
                isMediumBreakpoint={!!isMediumBreakpoint}
              />
            </div>
          </div>

          {!isAndroidClient && (
            <Tracked.InternalLink
              name="NOTIFICATION_CENTRE"
              variant="none"
              pagePosition="Header"
              to={'/notification-centre/'}
              className="cursor-pointer rounded-lg"
            >
              <NotificationBell />
            </Tracked.InternalLink>
          )}

          <Tracked.ExternalLink
            pagePosition="Header"
            name={'Sign in' as TrackedButtonClickName}
            variant="none"
            href={profilePage}
            onClick={() => {
              if (!user.isLoggedIn) {
                new RegisterIntentEvent({
                  buttonName: 'PROFILE_ICON',
                  isBot,
                  pagePosition: 'Header'
                }).sendToGtm();
              }
            }}
          >
            <ProfileIcon color={profileIconColor} />
          </Tracked.ExternalLink>
        </section>
      </div>
    </header>
  );
};

export default MainHeader;
